import { FlexColumn, FlexRow, LabeledInput, MaterialIcon, StyledButton } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { ButtonGroup } from 'baseui/button-group';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'baseui/modal';
import { OnChangeParams } from 'baseui/select';
import { HeadingXSmall, LabelSmall } from 'baseui/typography';
import { memo, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRecoilValue } from 'recoil';
import { useCopyToClipboard } from 'usehooks-ts';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { createShareLink } from '../../services/publicService';
import { apiParamsState, isAllWorkspacesSelect, isPublicPageState } from '../../storage/app';
import { allGroupsState } from '../../storage/groups';
import { ShareLinkDuration } from '../../types/app';
import { Asset } from '../../types/asset';
import { LabeledEmailInput } from '../ui/LabeledEmailInput';
import { LabeledSelect } from '../ui/LabeledSelect';
import { AssetPicker } from './AssetPicker';

type Props = {
  onClose: () => void;
  isOpen: boolean;
  assetId?: string;
};

function ShareAssetsModal({ onClose, isOpen, assetId }: Props) {
  const apiParams = useRecoilValue(apiParamsState);
  const isPublicPage = useRecoilValue(isPublicPageState);
  const isAllWorkspacesSelected = useRecoilValue(isAllWorkspacesSelect);
  const groups = useRecoilValue(allGroupsState);
  const { theme, classes } = useStyles();
  const { showPositiveFeedback, showFailFeedback } = useFeedback();
  const [, copy] = useCopyToClipboard();
  const [selectedAssetId, setSelectedAssetId] = useState<string>();
  const [selectedGroupId, setSelectedGroupId] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [emailIsValid, setEmailIsValid] = useState(true);
  const [duration, setDuration] = useState<ShareLinkDuration>(ShareLinkDuration.fourHours);
  const [linkToShare, setLinkToShare] = useState('');

  const groupOptions = useMemo(() => {
    if (!groups) return;
    return groups.map((group) => ({ id: group._id, label: group.name }));
  }, [groups]);

  const onGroupChange = (params: OnChangeParams) => {
    params.option?.id ? setSelectedGroupId(params.option.id as string) : setSelectedGroupId(undefined);
  };

  const handleGroupChange = (params: OnChangeParams) => {
    setSelectedAssetId(undefined);
    onGroupChange(params);
  };

  const durationIndex = useMemo(() => {
    if (duration === ShareLinkDuration.ninetySixHours) return 5;
    if (duration === ShareLinkDuration.fourtyEigthHours) return 4;
    if (duration === ShareLinkDuration.twentyFourHours) return 3;
    if (duration === ShareLinkDuration.twelveHours) return 2;
    if (duration === ShareLinkDuration.eightHours) return 1;
    return 0;
  }, [duration]);

  const canSubmit = (!!selectedAssetId || !!selectedGroupId) && !!emailIsValid;

  useEffect(() => {
    if (!assetId || !isOpen) return;
    setSelectedAssetId(assetId);
  }, [assetId, isOpen]);

  const generateShareLink = async () => {
    if ((!selectedAssetId && !selectedGroupId) || !apiParams) return;
    setLoading(true);
    const { error, link } = await createShareLink({
      clientId: apiParams.clientId,
      subClientId: apiParams.subClientId,
      assetId: selectedAssetId,
      groupId: selectedGroupId,
      hours: duration,
      email: email || undefined,
    });
    if (!error && link) {
      setLinkToShare(link);
    } else if (error) {
      showFailFeedback(error);
    }
    setLoading(false);
  };

  const resetModal = () => {
    onClose();
    setSelectedAssetId(undefined);
    setSelectedGroupId(undefined);
    setEmail('');
    setDuration(ShareLinkDuration.fourHours);
  };

  const handleAssetChange = (asset: Asset) => {
    setSelectedGroupId(undefined);
    setSelectedAssetId(asset._id);
  };

  const copyLinkToClipboard = async () => {
    const copied = await copy(linkToShare);
    if (copied) {
      showPositiveFeedback('Se copió el vínculo al portapapeles.');
    } else {
      showFailFeedback('No se pudo copiar, inténtalo más tarde.');
    }
  };

  if (isAllWorkspacesSelected || isPublicPage) {
    return null;
  }

  return createPortal(
    <Modal
      animate
      closeable
      onClose={resetModal}
      isOpen={isOpen}
      size={600}
    >
      <ModalHeader>
        <FlexRow
          gap={theme.sizing.scale200}
          classNames={classes.centeredStart}
        >
          <HeadingXSmall margin={0}>Compartir vínculo</HeadingXSmall>
        </FlexRow>
      </ModalHeader>
      <ModalBody>
        <FlexColumn>
          <AssetPicker
            assetId={selectedAssetId}
            placeholder="Selecciona una opción..."
            onChange={handleAssetChange}
            disabled={!!assetId}
          />
          {!assetId && (
            <LabeledSelect
              label="ó Selecciona un grupo:"
              options={groupOptions}
              placeholder="Selecciona una opción..."
              value={selectedGroupId ? [{ id: selectedGroupId }] : undefined}
              onChange={handleGroupChange}
              clearable
              searchable
            />
          )}
          <FlexColumn gap={theme.sizing.scale200}>
            <LabelSmall>Compartir durante:</LabelSmall>
            <FlexRow
              gap={theme.sizing.scale300}
              classNames={classes.centeredStart}
            >
              <ButtonGroup
                size="mini"
                mode="radio"
                selected={durationIndex}
                onClick={(_event, index) => {
                  if (index === 0) {
                    setDuration(ShareLinkDuration.fourHours);
                  } else if (index === 1) {
                    setDuration(ShareLinkDuration.eightHours);
                  } else if (index === 2) {
                    setDuration(ShareLinkDuration.twelveHours);
                  } else if (index === 3) {
                    setDuration(ShareLinkDuration.twentyFourHours);
                  } else if (index === 4) {
                    setDuration(ShareLinkDuration.fourtyEigthHours);
                  } else {
                    setDuration(ShareLinkDuration.ninetySixHours);
                  }
                }}
              >
                <Button>{ShareLinkDuration.fourHours}</Button>
                <Button>{ShareLinkDuration.eightHours}</Button>
                <Button>{ShareLinkDuration.twelveHours}</Button>
                <Button>{ShareLinkDuration.twentyFourHours}</Button>
                <Button>{ShareLinkDuration.fourtyEigthHours}</Button>
                <Button>{ShareLinkDuration.ninetySixHours}</Button>
              </ButtonGroup>
              <LabelSmall>horas</LabelSmall>
            </FlexRow>
          </FlexColumn>
          <LabeledEmailInput
            label="Correo electrónico:"
            placeholder="Ingresa un correo electrónico (opcional)"
            value={email}
            onChange={(value) => {
              setEmail(value);
            }}
            isEmailValid={setEmailIsValid}
          />
        </FlexColumn>
      </ModalBody>
      <ModalFooter>
        <FlexColumn gap={theme.sizing.scale300}>
          <LabeledInput
            value={linkToShare}
            placeholder="Todavía no se ha generado un vínculo."
            readonly
            endEnhancer={
              <Button
                size="mini"
                kind="tertiary"
                disabled={!linkToShare}
                onClick={copyLinkToClipboard}
              >
                <MaterialIcon
                  name="copy_all"
                  size="16px"
                  color={!linkToShare ? theme.colors.buttonDisabledText : theme.colors.buttonSecondaryText}
                />
              </Button>
            }
          />
          <StyledButton
            onClick={generateShareLink}
            disabled={!canSubmit}
            isLoading={loading}
          >
            Generar vínculo
          </StyledButton>
          <StyledButton
            kind="tertiary"
            onClick={resetModal}
          >
            {linkToShare ? 'Cerrar' : 'Cancelar'}
          </StyledButton>
        </FlexColumn>
      </ModalFooter>
    </Modal>,
    document.body,
  );
}

export default memo(ShareAssetsModal);
